/**
 * SuperAdmin Data-Pipeline Health service.
 *
 * Wraps /admin/super/data-health[/*] — the instrument-data lifecycle dashboard
 * (master download → S3 → ECS cache → service health) plus operator controls
 * (trigger a master download, reload an ECS cache, run a live LTP test). All
 * endpoints are SuperAdmin-gated server-side; this is a thin client.
 */
import apiClient from './apiClient';
import type { ApiResponse } from '../types';

const API_TYPE = 'api' as const;

export interface CacheStat {
  count: number;
  by_exchange: Record<string, number>;
  loaded_at: string | null;
}
export interface TaskHealth {
  task_id: string;
  ip: string;
  ok: boolean;
  error?: string;
  health?: Record<string, any>;
  cache_stats?: Record<string, CacheStat> | { status?: string; error?: string };
}
export interface ServiceHealth {
  health: Record<string, any>;
  cache_stats: Record<string, CacheStat> | { status?: string; error?: string };
  tasks?: TaskHealth[];
  task_count?: number;
  reachable?: number;
  via?: 'tasks' | 'alb';
}
export interface RefreshManifest {
  broker?: string;
  status: string; // "success" | "error" | "missing"
  total?: number;
  by_exchange?: Record<string, number>;
  timestamp?: string;
  error?: string;
}
export interface S3Master {
  exists: boolean;
  size_bytes?: number;
  last_modified?: string;
  error?: string;
}
export interface DataHealth {
  refresh: Record<string, RefreshManifest>;
  s3: Record<string, S3Master>;
  services: { broker_api: ServiceHealth; market_data: ServiceHealth };
  generated_at: string;
}
export interface LtpLeg {
  ok: boolean;
  ltp?: number;
  price?: number;
  error?: string;
}
export interface LtpTestResult {
  broker: string;
  exchange: string;
  symbol: string;
  broker_ltp: LtpLeg;
  md_ltp: LtpLeg;
}
export interface InstrumentMatch {
  exchange: string;
  symbol: string;
  token: string | number;
}
export interface InstrumentSearchResult {
  matches: InstrumentMatch[];
  total: number;
  limit: number;
}

export interface PlaceOrderPayload {
  client_id: string;
  broker: string;
  exchange: string;
  symbol: string;
  transaction_type: string;
  product: string;
  quantity: number;
  limit_price: number;
  tag?: string;
  is_marketplace: boolean;
}
export interface PlaceOrderResult {
  broker: string;
  user_id: string;
  client_id: string;
  symbol: string;
  exchange: string;
  transaction_type: string;
  product: string;
  quantity: number;
  limit_price: number;
  order_type: string;
  variety: string;
  is_marketplace: boolean;
  tag: string;
  order_placed: boolean;
  order_uncertain: boolean;
  error_message: string;
  result: Record<string, any>;
}

function unwrap<T>(r: ApiResponse<T>, what: string): T {
  if (!r.success || r.data === undefined) {
    throw new Error(r.message || `Failed to ${what}`);
  }
  return r.data as T;
}

class DataHealthService {
  async getHealth(): Promise<DataHealth> {
    const r = await apiClient.get<ApiResponse<DataHealth>>(
      '/admin/super/data-health', undefined, API_TYPE);
    return unwrap(r, 'load data health');
  }

  async testLtp(payload: {
    broker: string; user_id: string; client_id: string; exchange: string; symbol: string;
  }): Promise<LtpTestResult> {
    const r = await apiClient.post<ApiResponse<LtpTestResult>>(
      '/admin/super/data-health/test-ltp', payload, undefined, API_TYPE);
    return unwrap(r, 'test LTP');
  }

  async refreshMaster(broker: string): Promise<Record<string, any>> {
    const r = await apiClient.post<ApiResponse<Record<string, any>>>(
      '/admin/super/data-health/refresh-master', { broker }, undefined, API_TYPE);
    return unwrap(r, 'download master');
  }

  async reloadCache(service: string): Promise<Record<string, any>> {
    const r = await apiClient.post<ApiResponse<Record<string, any>>>(
      '/admin/super/data-health/reload-cache', { service }, undefined, API_TYPE);
    return unwrap(r, 'reload cache');
  }

  async searchInstruments(payload: {
    service: string; broker: string; exchange: string; query: string; limit?: number;
  }): Promise<InstrumentSearchResult> {
    const r = await apiClient.post<ApiResponse<InstrumentSearchResult>>(
      '/admin/super/data-health/instruments', payload, undefined, API_TYPE);
    return unwrap(r, 'search instruments');
  }

  async placeOrder(payload: PlaceOrderPayload): Promise<PlaceOrderResult> {
    const r = await apiClient.post<ApiResponse<PlaceOrderResult>>(
      '/admin/super/data-health/place-order', payload, undefined, API_TYPE);
    return unwrap(r, 'place order');
  }
}

const dataHealthService = new DataHealthService();
export default dataHealthService;
